/**
 * Curated font configuration for the app.
 * Includes local fonts and Google Fonts selection.
 */

export interface FontOption {
  id: string
  name: string
  family: string
  source: 'local' | 'google'
  // Google Font specific
  googleFontUrl?: string
  // Preview weight for the selector
  weight?: number
  // Category for grouping
  category: 'monospace' | 'sans-serif' | 'serif' | 'display' | 'accessibility'
}

/**
 * Curated selection of fonts.
 * Local fonts are always available, Google Fonts load on-demand.
 */
export const FONT_OPTIONS: FontOption[] = [
  // === Local Fonts (Always Available) ===
  {
    id: 'departure-mono',
    name: 'Departure Mono',
    family: '"Departure Mono", "Noto Emoji", monospace',
    source: 'local',
    category: 'monospace',
  },
  {
    id: 'open-dyslexic',
    name: 'OpenDyslexic',
    family: '"OpenDyslexic", "Noto Emoji", Arial, sans-serif',
    source: 'local',
    category: 'accessibility',
  },

  // === Monospace (coding feel) ===
  {
    id: 'jetbrains-mono',
    name: 'JetBrains Mono',
    family: '"JetBrains Mono", "Noto Emoji", monospace',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap',
    category: 'monospace',
  },
  {
    id: 'fira-code',
    name: 'Fira Code',
    family: '"Fira Code", "Noto Emoji", monospace',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;600&display=swap',
    category: 'monospace',
  },
  {
    id: 'space-mono',
    name: 'Space Mono',
    family: '"Space Mono", "Noto Emoji", monospace',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap',
    category: 'monospace',
  },
  {
    id: 'ibm-plex-mono',
    name: 'IBM Plex Mono',
    family: '"IBM Plex Mono", "Noto Emoji", monospace',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&display=swap',
    category: 'monospace',
  },
  {
    id: 'source-code-pro',
    name: 'Source Code Pro',
    family: '"Source Code Pro", "Noto Emoji", monospace',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;500;600&display=swap',
    category: 'monospace',
  },

  // === Clean Sans-Serif ===
  {
    id: 'inter',
    name: 'Inter',
    family: '"Inter", "Noto Emoji", system-ui, sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
    category: 'sans-serif',
  },
  {
    id: 'dm-sans',
    name: 'DM Sans',
    family: '"DM Sans", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap',
    category: 'sans-serif',
  },
  {
    id: 'outfit',
    name: 'Outfit',
    family: '"Outfit", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600&display=swap',
    category: 'sans-serif',
  },
  {
    id: 'work-sans',
    name: 'Work Sans',
    family: '"Work Sans", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600&display=swap',
    category: 'sans-serif',
  },

  // === Display / Unique ===
  {
    id: 'space-grotesk',
    name: 'Space Grotesk',
    family: '"Space Grotesk", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600&display=swap',
    category: 'display',
  },
  {
    id: 'syne',
    name: 'Syne',
    family: '"Syne", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600&display=swap',
    category: 'display',
  },
  {
    id: 'archivo',
    name: 'Archivo',
    family: '"Archivo", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600&display=swap',
    category: 'display',
  },
  {
    id: 'lexend',
    name: 'Lexend',
    family: '"Lexend", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600&display=swap',
    category: 'display',
  },
  {
    id: 'rubik',
    name: 'Rubik',
    family: '"Rubik", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600&display=swap',
    category: 'display',
  },

  // === Serif / Classic ===
  {
    id: 'source-serif-4',
    name: 'Source Serif 4',
    family: '"Source Serif 4", "Noto Emoji", Georgia, serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;500;600&display=swap',
    category: 'serif',
  },
  {
    id: 'bitter',
    name: 'Bitter',
    family: '"Bitter", "Noto Emoji", Georgia, serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Bitter:wght@400;500;600&display=swap',
    category: 'serif',
  },
  {
    id: 'lora',
    name: 'Lora',
    family: '"Lora", "Noto Emoji", Georgia, serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600&display=swap',
    category: 'serif',
  },
  {
    id: 'new-york',
    name: 'New York',
    // Apple system serif with sensible fallbacks
    family: '"New York", "Times New Roman", "Noto Emoji", serif',
    source: 'local',
    category: 'serif',
  },

  // === Accessibility ===
  {
    id: 'atkinson-hyperlegible',
    name: 'Atkinson Hyperlegible',
    family: '"Atkinson Hyperlegible", "Noto Emoji", sans-serif',
    source: 'google',
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap',
    category: 'accessibility',
  },
]

export const DEFAULT_FONT_ID = 'departure-mono'

export const FONT_STORAGE_KEY = 'kyogre-selected-font'

/**
 * Get a font option by ID
 */
export function getFontById(id: string): FontOption | undefined {
  return FONT_OPTIONS.find(f => f.id === id)
}

/**
 * Get the default font option
 */
export function getDefaultFont(): FontOption {
  return FONT_OPTIONS[0]
}

/**
 * Group fonts by category for the selector UI
 */
export function getFontsByCategory(): Record<string, FontOption[]> {
  const grouped: Record<string, FontOption[]> = {}
  for (const font of FONT_OPTIONS) {
    if (!grouped[font.category]) {
      grouped[font.category] = []
    }
    grouped[font.category].push(font)
  }
  return grouped
}

/**
 * Category display names
 */
export const CATEGORY_LABELS: Record<string, string> = {
  monospace: 'Monospace',
  'sans-serif': 'Sans Serif',
  serif: 'Serif',
  display: 'Display',
  accessibility: 'Accessibility',
}

